import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ColumnFilterPanelBase from '@/components/CustomizableTable/ColumnFilterPanel';
import ColumnFilterControls from '@/components/CustomizableTable/ColumnFilterControls';
import { wh_statuses, owner_options } from './filterOptions';
import { LOCATION_TYPES } from '@/constants/locationTypes';
import { useClientUser } from '@/hooks';

export const COLUMN_FILTER_MAP = {
    dropoff: {
        title: 'Drop Off',
        controls: [{ type: 'multiSelect', key: 'selectedZips', label: 'Zip Codes', dynamicOptions: 'zipCodes' }],
    },
    service_type: {
        title: 'Service Type',
        controls: [{ type: 'serviceType', key: 'serviceType', label: 'Service Type' }],
    },
    warehousestatus: {
        title: 'Warehouse Status',
        controls: [{ type: 'select', key: 'warehouseStatus', label: 'Warehouse Status', options: wh_statuses }],
    },
    received_date: {
        title: 'Actual Received Date',
        controls: [
            {
                type: 'dateRange',
                keys: ['warehouseFrom', 'warehouseTo'],
                label: 'Warehouse Actual Received Date',
            },
        ],
    },
    owner: {
        title: 'Owner',
        controls: [{ type: 'select', key: 'ownerType', label: 'Owner', options: owner_options }],
    },
    created_at: {
        title: 'Order Creation Date',
        controls: [{ type: 'dateRange', keys: ['createdFrom', 'createdTo'], label: 'Created Between' }],
    },
    preferred_delivery_date: {
        title: 'Preferred Delivery Date',
        controls: [
            { type: 'dateRange', keys: ['prefDateFrom', 'prefDateTo'], label: 'Preferred Delivery Date Between' },
            { type: 'checkbox', key: 'hasPrefDate', label: 'Has a preferred delivery date' },
        ],
    },
};

const DispatchUnassignedColumnFilterPanel = ({
    open,
    columnId,
    onClose,
    applyFilters,
    filters,
    orders = [],
    accountType,
}) => {
    const { service_levels } = useClientUser();

    const serviceTypes = useMemo(() => {
        return [
            ...Object.entries(LOCATION_TYPES).map(([value, label]) => ({ label, value })),
            ...(service_levels || []).map(({ service_level_id, service_level }) => ({
                label: service_level,
                value: service_level_id,
            })),
        ];
    }, [service_levels]);

    const zipOptions = useMemo(() => {
        const zips = new Set();
        (orders || []).forEach((order) => {
            if (order.dropoff_zip) zips.add(order.dropoff_zip);
            if (order.pickup_zip) zips.add(order.pickup_zip);
        });
        return Array.from(zips)
            .sort()
            .map((zip) => ({ value: zip, label: zip }));
    }, [orders]);

    const [localFilters, setLocalFilters] = useState({});

    const config = COLUMN_FILTER_MAP[columnId];

    useEffect(() => {
        if (!open || !config) return;

        const next = {};
        config.controls.forEach((control) => {
            if (control.type === 'dateRange' && Array.isArray(control.keys)) {
                control.keys.forEach((key) => {
                    next[key] = filters?.[key] || '';
                });
            } else if ((control.type === 'select' || control.type === 'checkbox') && control.key) {
                next[control.key] = filters?.[control.key] ?? (control.type === 'checkbox' ? false : '');
            } else if (control.type === 'multiSelect' && control.key) {
                next[control.key] = filters?.[control.key] || [];
            } else if (control.type === 'serviceType') {
                next.serviceType = filters?.serviceType || '';
                next.locationType = filters?.locationType || '';
            }
        });

        setLocalFilters(next);
    }, [open, config, filters]);

    const setValue = useCallback((key, value) => {
        setLocalFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    const resolveOptions = useCallback(
        (control) => {
            if (control.type === 'serviceType') return serviceTypes;
            if (control.type === 'select') return control.options || [];
            if (control.type === 'multiSelect' && control.dynamicOptions === 'zipCodes') return zipOptions;
            return control.options || [];
        },
        [serviceTypes, zipOptions]
    );

    const handleApply = useCallback(() => {
        if (!config) return;
        const payload = {};
        config.controls.forEach((control) => {
            if (control.type === 'dateRange') {
                control.keys.forEach((key) => {
                    payload[key] = localFilters[key] || null;
                });
            } else if (control.type === 'select' || control.type === 'checkbox') {
                const val = localFilters[control.key];
                payload[control.key] = control.type === 'checkbox' ? Boolean(val) : val || null;
            } else if (control.type === 'multiSelect') {
                payload[control.key] = localFilters[control.key] || [];
            } else if (control.type === 'serviceType') {
                payload.serviceType = localFilters.serviceType || null;
                payload.locationType = localFilters.locationType || null;
            }
        });
        applyFilters(payload);
    }, [config, localFilters, applyFilters]);

    const handleClear = useCallback(() => {
        if (!config) return;
        const payload = {};
        config.controls.forEach((control) => {
            if (control.type === 'dateRange') {
                control.keys.forEach((key) => {
                    payload[key] = null;
                });
            } else if (control.type === 'select') {
                payload[control.key] = null;
            } else if (control.type === 'checkbox') {
                payload[control.key] = false;
            } else if (control.type === 'multiSelect') {
                payload[control.key] = [];
            } else if (control.type === 'serviceType') {
                payload.serviceType = null;
                payload.locationType = null;
            }
        });
        setLocalFilters((prev) => {
            const cleared = { ...prev };
            Object.keys(payload).forEach((key) => {
                cleared[key] = Array.isArray(payload[key]) ? [] : payload[key];
            });
            return cleared;
        });
        applyFilters(payload);
    }, [config, applyFilters]);

    if (!config) return null;

    return (
        <ColumnFilterPanelBase
            open={open}
            columnId={columnId}
            title={config.title}
            onClose={onClose}
            onApply={handleApply}
            onClear={handleClear}
            topOffset={50}
        >
            <ColumnFilterControls
                controls={config.controls}
                values={localFilters}
                setValue={setValue}
                resolveOptions={resolveOptions}
                locationTypesMap={LOCATION_TYPES}
            />
        </ColumnFilterPanelBase>
    );
};

export default DispatchUnassignedColumnFilterPanel;
